import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { Avatar, Button } from '../ui';
import PremiumName from '../ui/PremiumName';
import { useAuth } from '../../hooks/useAuth';
import { followUser, unfollowUser } from '../../services/userService';

/**
 * TrendingArtists - Sidebar/Sección de artistas destacados
 * Muestra usuarios con más seguidores
 */
const TrendingArtists = ({ variant = 'card', title = 'Artistas destacados', limitCount = 16 }) => {
  const { currentUser, userData } = useAuth();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingIds, setFollowingIds] = useState(userData?.following || []);

  useEffect(() => {
    setFollowingIds(userData?.following || []);
  }, [userData?.following]);

  useEffect(() => {
    const loadTrendingArtists = async () => {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, orderBy('followersCount', 'desc'), limit(limitCount));
        const snapshot = await getDocs(q);
        
        const loadedArtists = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(user => user.id !== currentUser?.uid);
        
        setArtists(loadedArtists);
      } catch (error) {
        console.error('Error al cargar artistas destacados:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadTrendingArtists();
  }, [currentUser?.uid, limitCount]);

  const handleFollow = async (artistId) => {
    if (!currentUser) return;
    
    const isFollowing = followingIds.includes(artistId);
    
    // Optimistic update
    setFollowingIds(prev => 
      isFollowing 
        ? prev.filter(id => id !== artistId)
        : [...prev, artistId]
    );

    const result = isFollowing 
      ? await unfollowUser(artistId)
      : await followUser(artistId);

    if (!result.success) {
      // Revertir en caso de error
      setFollowingIds(prev => 
        isFollowing 
          ? [...prev, artistId]
          : prev.filter(id => id !== artistId)
      );
    }
  };

  if (loading) {
    if (variant === 'horizontal') {
      return (
        <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="flex-shrink-0 w-56 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-3 animate-pulse"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 dark:bg-slate-700 rounded-full" />
                <div className="flex-1">
                  <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-24 mb-1" />
                  <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded w-16" />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
          {title}
        </h3>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-10 h-10 bg-gray-200 dark:bg-slate-700 rounded-full" />
              <div className="flex-1">
                <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-24 mb-1" />
                <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded w-16" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (artists.length === 0) return null;

  if (variant === 'circles') {
    return (
      <div className="flex gap-4 overflow-x-auto pb-2 hide-scrollbar">
        {artists.map((artist) => {
          const isFollowing = followingIds.includes(artist.id);
          const name = artist.displayName || artist.username || 'Usuario';
          const photo = artist.photoURL || artist.profileImage || null;
          return (
            <div key={artist.id} className="flex-shrink-0 w-20 text-center">
              <Link
                to={`/profile/${artist.id}`}
                className="inline-flex flex-col items-center gap-2 hover:opacity-90 transition-opacity"
              >
                <Avatar
                  src={photo}
                  alt={name}
                  fallbackText={name}
                  size="xl"
                  showRing
                />
                <PremiumName
                  name={name}
                  isPremium={artist.isPremium}
                  premiumColor={artist.premiumNameColor}
                  className="w-full justify-center"
                  nameClassName="text-xs font-medium text-gray-900 dark:text-white truncate block w-full"
                />
              </Link>
              <button
                type="button"
                onClick={() => handleFollow(artist.id)}
                className={`mt-2 w-full text-[11px] font-medium px-2 py-1 rounded-full border transition-colors ${
                  isFollowing
                    ? 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'
                    : 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {isFollowing ? 'Siguiendo' : 'Seguir'}
              </button>
            </div>
          );
        })}
      </div>
    );
  }

  if (variant === 'horizontal') {
    return (
      <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
        {artists.map((artist) => {
          const isFollowing = followingIds.includes(artist.id);
          const name = artist.displayName || artist.username || 'Usuario';
          const photo = artist.photoURL || artist.profileImage || null;
          return (
            <div
              key={artist.id}
              className="flex-shrink-0 w-56 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-3"
            >
              <div className="flex items-center gap-3">
                <Link
                  to={`/profile/${artist.id}`}
                  className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                >
                  <Avatar
                    src={photo}
                    alt={name}
                    fallbackText={name}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <PremiumName
                      name={name}
                      isPremium={artist.isPremium}
                      premiumColor={artist.premiumNameColor}
                      nameClassName="text-sm font-medium text-gray-900 dark:text-white truncate block"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {artist.followersCount || 0} seguidores
                    </p>
                  </div>
                </Link>
              </div>

              <div className="mt-3">
                <Button
                  variant={isFollowing ? 'secondary' : 'primary'}
                  size="sm"
                  onClick={() => handleFollow(artist.id)}
                  className="w-full text-xs px-3 py-1"
                >
                  {isFollowing ? 'Siguiendo' : 'Seguir'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 sticky top-20">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
        {title}
      </h3>
      <div className="space-y-3">
        {artists.map((artist) => {
          const isFollowing = followingIds.includes(artist.id);
          const name = artist.displayName || artist.username || 'Usuario';
          const photo = artist.photoURL || artist.profileImage || null;
          
          return (
            <div key={artist.id} className="flex items-center gap-3">
              <Link
                to={`/profile/${artist.id}`}
                className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
              >
                <Avatar
                  src={photo}
                  alt={name}
                  fallbackText={name}
                  size="sm"
                />
                <div className="flex-1 min-w-0">
                  <PremiumName
                    name={name}
                    isPremium={artist.isPremium}
                    premiumColor={artist.premiumNameColor}
                    nameClassName="text-sm font-medium text-gray-900 dark:text-white truncate block"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {artist.followersCount || 0} seguidores
                  </p>
                </div>
              </Link>
              
              <Button
                variant={isFollowing ? 'secondary' : 'primary'}
                size="sm"
                onClick={() => handleFollow(artist.id)}
                className="text-xs px-3 py-1"
              >
                {isFollowing ? 'Siguiendo' : 'Seguir'}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TrendingArtists;
